import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/", label: "Home" },
  { to: "/#divisions", label: "Divisions" },
  { to: "/#about", label: "About" },
  { to: "/#contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location]);

  const handleHashLink = (to) => {
    if (to.includes("#")) {
      const [path, hash] = to.split("#");
      if (location.pathname === (path || "/")) {
        const el = document.querySelector(`#${hash}`);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate(to);
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-slate-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_business-nexus-54/artifacts/695zxc2t_WhatsApp%20Image%202026-06-09%20at%203.16.25%20PM.jpeg"
              alt="Indo-Pacific Group"
              className="h-12 w-12 object-contain rounded-lg"
            />
            <span className="font-heading text-lg font-medium tracking-tight text-slate-900">
              Indo-Pacific
              <span className="text-[#0284C7]"> Group</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => handleHashLink(l.to)}
                className="text-sm font-medium text-slate-600 hover:text-[#0A2540] transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Button
              data-testid="nav-book-appointment"
              onClick={() => navigate("/book")}
              className="rounded-full bg-[#0A2540] hover:bg-[#0F172A] text-white h-11 px-5"
            >
              Book Appointment
            </Button>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-slate-200 text-slate-700"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {open && (
          <div
            data-testid="nav-mobile-panel"
            className="md:hidden pb-6 border-t border-slate-200/60 pt-4 space-y-2"
          >
            {links.map((l) => (
              <button
                key={l.to}
                onClick={() => handleHashLink(l.to)}
                className="block w-full text-left px-2 py-3 text-base font-medium text-slate-700 hover:text-[#0A2540]"
              >
                {l.label}
              </button>
            ))}
            <Button
              data-testid="nav-mobile-book"
              onClick={() => navigate("/book")}
              className="w-full rounded-full bg-[#0A2540] hover:bg-[#0F172A] text-white h-12 mt-2"
            >
              Book Appointment
            </Button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
